import { useFormik } from 'formik';
import toast from 'react-hot-toast';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import BetterBreadcrumb from 'src/components/Common/BetterBreadCrumb';
import {
  useGetAdminKbCategoriesQuery,
  useGetAdminKbQuestionQuery,
  useUpdateKbQuestionMutation,
} from 'src/rtk/features/chatbot/chatbotApi';
import * as Yup from 'yup';

const schema = Yup.object({
  question: Yup.string().required('Question is required'),
  answer: Yup.string().required('Answer is required'),
  status: Yup.string().oneOf(['draft', 'published']),
  category_id: Yup.number().nullable(),
  tags_str: Yup.string(),
});

const KnowledgeBaseEditPage = () => {
  const { id } = useParams<{ id: string }>();
  document.title = 'Edit Question - Knowledge Base | BCPS';
  const history = useHistory();

  const { data: questionData, isLoading: loadingQuestion } = useGetAdminKbQuestionQuery(id!, { skip: !id });
  const question = questionData?.data;

  const [updateQuestion, { isLoading }] = useUpdateKbQuestionMutation();

  const formik = useFormik({
    initialValues: {
      question: question?.question ?? '',
      answer: question?.answer ?? '',
      status: (question?.status as 'draft' | 'published') ?? 'draft',
    },
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (!id) return;
      try {
        await updateQuestion({
          id,
          question: values.question,
          answer: values.answer,
          status: values.status,
        }).unwrap();
        toast.success('Question updated.');
        history.push('/admin/knowledge-base/questions');
      } catch (e: any) {
        const apiErrors = e?.data?.errors;
        if (apiErrors && typeof apiErrors === 'object') {
          const message = Object.values(apiErrors).flat().join(' ');
          toast.error(message || e?.data?.message || 'Failed to update.');
        } else {
          toast.error(e?.data?.message || 'Failed to update.');
        }
      }
    },
  });

  const { values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue } = formik;

  if (!id) {
    return (
      <div className="page-content">
        <Container fluid>
          <p>Invalid ID.</p>
          <Button color="primary" onClick={() => history.push('/admin/knowledge-base/questions')}>
            Back to list
          </Button>
        </Container>
      </div>
    );
  }

  if (loadingQuestion || (questionData && !question)) {
    return (
      <div className="page-content">
        <Container fluid>
          <p>{loadingQuestion ? 'Loading...' : 'Question not found.'}</p>
          <Button color="primary" onClick={() => history.push('/admin/knowledge-base/questions')}>
            Back to list
          </Button>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BetterBreadcrumb
          title="Edit Question"
          breadcrumbItem={[
            { link: '/admin/knowledge-base/questions', name: 'Knowledge Base' },
          ]}
        />
        <Card>
          <CardBody>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={12}>
                  <FormGroup>
                    <Label for="question">Question *</Label>
                    <Input
                      id="question"
                      name="question"
                      type="textarea"
                      rows={3}
                      value={values.question}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={!!(errors.question && touched.question)}
                    />
                    {errors.question && touched.question && (
                      <div className="text-danger small">{String(errors.question)}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup>
                    <Label for="answer">Answer *</Label>
                    <Input
                      id="answer"
                      name="answer"
                      type="textarea"
                      rows={5}
                      value={values.answer}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={!!(errors.answer && touched.answer)}
                    />
                    {errors.answer && touched.answer && (
                      <div className="text-danger small">{String(errors.answer)}</div>
                    )}
                  </FormGroup>
                </Col>
                <Col md={2}>
                  <FormGroup>
                    <Label for="status">Status</Label>
                    <Input
                      id="status"
                      name="status"
                      type="select"
                      value={values.status}
                      onChange={(e) => setFieldValue('status', e.target.value)}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </Input>
                  </FormGroup>
                </Col>
                
              </Row>
              <div className="d-flex gap-2 mt-3">
                <Button type="submit" color="primary" disabled={isLoading} className='px-4'>
                  {isLoading ? 'Saving...' : 'Update'}
                </Button>
                <Button type="button" color="secondary" outline onClick={() => history.push('/admin/knowledge-base/questions')} className='px-4'>
                  Cancel
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default KnowledgeBaseEditPage;
